import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { AutosaveState } from './ClinicalEditorShell';
import { CHARACTER_LIMIT } from './extensions';

interface Props {
  autosaveState: AutosaveState;
  wordCount: number;
  characterCount: number;
  focusMode: boolean;
  onToggleFocus: () => void;
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'primary' | 'destructive';
  };
}

const SAVE_TEXT: Record<AutosaveState, string> = {
  idle: 'Up to date',
  dirty: 'Unsaved',
  saving: 'Saving…',
  saved: 'Saved',
  failed: 'Save failed',
};

// Dot tone + a paired soft glow so the save indicator carries weight beyond
// being a flat circle. Saving pulses; failed sits steady-red so it doesn't
// read as "in flight".
const SAVE_DOT: Record<AutosaveState, string> = {
  idle: 'bg-primary-400 shadow-[0_0_0_3px_rgba(2,97,74,0.12)]',
  dirty: 'bg-warning-500 shadow-[0_0_0_3px_rgba(217,119,6,0.15)]',
  saving: 'bg-primary-300 shadow-[0_0_0_3px_rgba(53,129,110,0.18)] animate-pulse',
  saved: 'bg-primary-400 shadow-[0_0_0_3px_rgba(2,97,74,0.18)]',
  failed: 'bg-error-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]',
};

const WARN_THRESHOLD = Math.floor(CHARACTER_LIMIT * 0.95);

interface KbdProps {
  children: React.ReactNode;
}

// Printed-page kbd chip — soft cream surface, fine accent-ink ring, mono
// type. Smaller and more refined than a generic <kbd> with a default UA
// border.
function Kbd({ children }: KbdProps) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1 font-mono text-[10px] tabular-nums',
        'text-accent-ink/80 ring-accent-ink/12 bg-white shadow-[0_1px_0_rgba(3,68,92,0.04)] ring-1',
      )}
    >
      {children}
    </kbd>
  );
}

export function EditorFooter({
  autosaveState,
  wordCount,
  characterCount,
  focusMode,
  onToggleFocus,
  primaryAction,
}: Props) {
  const showCharCounter = characterCount >= WARN_THRESHOLD;
  const atLimit = characterCount >= CHARACTER_LIMIT;
  const charLabel = atLimit
    ? 'Limit reached'
    : `${String(characterCount)} / ${String(CHARACTER_LIMIT)}`;

  return (
    <footer className="border-accent-ink/10 sticky bottom-0 z-10 flex h-11 items-center justify-between gap-3 border-t bg-white/95 px-5 backdrop-blur-sm">
      <div className="flex items-center gap-3 text-[12px] text-neutral-500">
        <span className="inline-flex items-center gap-2" aria-live="polite">
          <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', SAVE_DOT[autosaveState])} />
          <span className="text-accent-ink/75">{SAVE_TEXT[autosaveState]}</span>
        </span>
        <span aria-hidden className="text-accent-ink/15">
          ·
        </span>
        <span className="font-mono text-[11px] text-neutral-500 tabular-nums">
          {`${String(wordCount)} words`}
        </span>
        {showCharCounter ? (
          <>
            <span aria-hidden className="text-accent-ink/15">
              ·
            </span>
            <span
              className={cn(
                'font-mono text-[11px] tabular-nums',
                atLimit ? 'text-error-500' : 'text-warning-500',
              )}
            >
              {charLabel}
            </span>
          </>
        ) : null}
      </div>
      <p className="hidden items-center gap-2.5 text-[11px] text-neutral-500 lg:inline-flex">
        <span className="inline-flex items-center gap-1.5">
          <Kbd>⌘</Kbd>
          <Kbd>S</Kbd>
          <span className="text-neutral-400">save</span>
        </span>
        <span aria-hidden className="text-accent-ink/15">
          ·
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>⌘</Kbd>
          <Kbd>↵</Kbd>
          <span className="text-neutral-400">submit</span>
        </span>
        <span aria-hidden className="text-accent-ink/15">
          ·
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>?</Kbd>
          <span className="text-neutral-400">shortcuts</span>
        </span>
      </p>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onToggleFocus}
          aria-pressed={focusMode}
          aria-label={focusMode ? 'Exit focus mode' : 'Enter focus mode'}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium transition-all',
            'focus-visible:ring-primary-200 ring-1 focus-visible:ring-2 focus-visible:outline-none',
            focusMode
              ? 'bg-accent-ink ring-accent-ink/20 shadow-accent-ink/15 text-white shadow-sm'
              : 'text-accent-ink/70 ring-accent-ink/10 hover:bg-primary-50 hover:text-accent-ink hover:ring-primary-200/60',
          )}
        >
          {focusMode ? (
            <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
          ) : (
            <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
          )}
          Focus
        </button>
        {primaryAction !== undefined ? (
          <Button
            variant={primaryAction.variant === 'destructive' ? 'destructive' : 'primary'}
            size="sm"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled === true}
            className="shadow-primary-700/10 hover:shadow-primary-700/15 rounded-full px-4 shadow-sm transition-shadow hover:shadow-md"
          >
            {primaryAction.label}
          </Button>
        ) : null}
      </div>
    </footer>
  );
}
